/**
 * The overview (APP_SPEC box 3): one card per project, every number a
 * projection the server counted and a link into the screen that owns
 * it. The "New project" card teaches the first step -- creating a
 * project IS naming one; the first upload mints it.
 *
 * 2026-09-17: GET /v1/projects is not in the contract; projects list is absent.
 * The viewer loads per-project data by ID via the URL; listing is not implemented.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AbsenceBadge, ActionButton, Field, Frame, Notice, Panel, RouteLink, Stack, Text, Title } from '../strata';

const PROJECT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

export function Overview() {
  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const isValidProjectId = PROJECT_ID_PATTERN.test(newName.trim());
  const openNew = () => {
    const name = newName.trim();
    if (isValidProjectId) {
      void navigate({ to: '/projects/$projectId', params: { projectId: name } });
      setNewName('');
    }
  };

  return (
    <Frame>
      <Stack direction="row" gap="xl" align="baseline" wrap>
        <Title level={3}>Projects</Title>
        <RouteLink to="/onboard" tone="brand" size="body">
          Onboard organization →
        </RouteLink>
      </Stack>
      <div>
        <AbsenceBadge label="No projects list" reason="GET /v1/projects is not in the contract" />
      </div>
      <Stack direction="row" gap="md" align="flex-start" wrap>
        <Panel width={280}>
          <Stack direction="column" gap="md">
            <Title level={4}>New project</Title>
            <Field
              label="New project name"
              labelHidden
              value={newName}
              onChange={setNewName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') openNew();
              }}
              placeholder="name it"
            />
            <ActionButton variant="brand" onPress={openNew} isDisabled={!isValidProjectId}>
              Open workspace
            </ActionButton>
            {newName.trim() && !isValidProjectId && (
              <Notice kind="error">Use letters, numbers, hyphens, or underscores (1-128 characters).</Notice>
            )}
            <Text tone="secondary" size="micro">
              The first upload creates the project.
            </Text>
          </Stack>
        </Panel>
      </Stack>
    </Frame>
  );
}
